import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BroadFieldsCard } from '../components/BroadFieldsCard';
import { MetingHerladen, Storing, WeekZonderOverzicht } from '../components/MeasurementState';
import { PeriodPicker } from '../components/PeriodPicker';
import { useMeldToestand } from '../components/Toestandsmelding';
import { WeekReportCard } from '../components/WeekReportCard';
import { useEenMeting } from '../hooks/useEenMeting';
import { useGekozenWeek } from '../hooks/useGekozenWeek';
import { useMedewerkers, useWeekrapport } from '../hooks/useOverzicht';
import { STORING_ONBEREIKBAAR, vorigeWeekVan } from '../toestand';
import { amsterdamseDag, maandagVanWeek, weeknummer } from '../week';
import type { Meetversies } from '../work-quality.api';

/**
 * AI (S6, `/werkkwaliteit/ai`) — vier AI-kerngetallen, Onderdeelbalken, Regressielijst en
 * Struikeltabel uit het bestaande weekrapport (CAP-8). Eén rapport voor beide werksoorten, dus geen
 * Werksoortschakelaar; de reikwijdtezin onder de Weekkiezer zegt dat. De struikelvelden komen uit het medewerkersoverzicht van die week
 * (`topVelden`), en daarom bewaakt AD-24 hier dat rapport en overzicht uit één meting komen.
 */
export function AiPage() {
  const { t } = useTranslation();
  const { week, weken, kies } = useGekozenWeek();
  const medewerkers = useMedewerkers(week);
  const rapport = useWeekrapport(week);

  const overzicht = medewerkers.data?.data ?? null;
  const weekrapport = rapport.data?.data ?? null;
  // Een week zonder weekrapport is geen tweede meting maar een ontbrekend blok.
  const onderdelen: Meetversies[] = [overzicht, weekrapport]
    .map((deel): Meetversies | undefined => deel?.afbakening)
    .filter((afbakening) => afbakening !== undefined);

  const { refetch: herlaadMedewerkers } = medewerkers;
  const { refetch: herlaadRapport } = rapport;
  const opnieuw = useCallback(() => {
    herlaadMedewerkers();
    herlaadRapport();
  }, [herlaadMedewerkers, herlaadRapport]);

  const lopenUiteen = useEenMeting(onderdelen, opnieuw);
  const vorigeWeek = vorigeWeekVan(weken, week);

  function inhoud() {
    if (weken.length === 0) return null;
    if (medewerkers.isLoading || rapport.isLoading) return <AiLaden />;
    if (medewerkers.isError || rapport.isError || medewerkers.data === undefined || rapport.data === undefined) {
      return <Storing oorzaak={STORING_ONBEREIKBAAR} vorigeWeek={vorigeWeek} />;
    }
    if (medewerkers.data.data === null) {
      return <WeekZonderOverzicht antwoord={medewerkers.data} vorigeWeek={vorigeWeek} week={week} weken={weken} />;
    }
    if (lopenUiteen) return <MetingHerladen onOpnieuw={opnieuw} />;
    if (weekrapport?.reden === 'geen_weekrapport') return <NogNietKlaar week={week} />;

    const overzichtsdata = medewerkers.data.data;
    return (
      <>
        <WeekReportCard rapport={weekrapport} topVeldenBerekenDatum={overzichtsdata.topVelden?.berekenDatum} />
        <BroadFieldsCard topVelden={overzichtsdata.topVelden} veldnamen={overzichtsdata.veldnamen} week={week} />
      </>
    );
  }

  const laden = weken.length > 0 && (medewerkers.isLoading || rapport.isLoading);

  return (
    <div className="flex min-w-0 flex-col gap-5" data-testid="werkkwaliteit-ai">
      <div className="flex flex-col gap-2">
        <PeriodPicker onKies={kies} waarde={week} weken={weken} />
        <p className="text-sm text-[var(--foreground-muted)]" data-testid="werkkwaliteit-ai-reikwijdte">
          {t('qa.ai.reikwijdte')}
        </p>
      </div>
      <div aria-busy={laden} className="flex min-w-0 flex-col gap-5" data-testid="werkkwaliteit-ai-inhoud">
        {inhoud()}
      </div>
    </div>
  );
}

/** Laden in de vorm van wat er komt: vier kerngetallen, nooit nullen als tussenstand. */
function AiLaden() {
  const { t } = useTranslation();
  useMeldToestand(t('qa.ai.laden'));

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="werkkwaliteit-laden" role="status">
      <span className="sr-only">{t('qa.ai.laden')}</span>
      {[0, 1, 2, 3].map((vak) => (
        <div
          aria-hidden="true"
          className="h-28 rounded-lg bg-[var(--background-muted)]"
          data-werkkwaliteit-skelet="kerngetal"
          key={vak}
        />
      ))}
    </div>
  );
}

/**
 * Het rapport over deze week is er (nog) niet. Het wordt elke maandag gemaakt over de week ervoor;
 * op die maandag (Amsterdamse kalenderdag) zegt het scherm dat het komt. Is die maandag voorbij, dan
 * belooft het niets meer — een belofte die niet uitkwam is erger dan geen belofte.
 */
function NogNietKlaar({ week }: { week: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const maandag = new Date(maandagVanWeek(week).getTime() + 7 * 86_400_000);
  // Een `YYYY-MM-DD`-vergelijking: de maandag is een UTC-middernacht, vandaag de dag in Amsterdam.
  const komtNog = amsterdamseDag(new Date()) <= maandag.toISOString().slice(0, 10);
  const kop = t('qa.ai.nogNietKlaarKop', { week: weeknummer(week) });
  const tekst = komtNog
    ? t('qa.ai.nogNietKlaarKomt', {
        week: weeknummer(week),
        datum: maandag.toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }),
      })
    : t('qa.ai.nogNietKlaarVoorbij');
  useMeldToestand(`${kop} ${tekst}`);

  return (
    <div
      className="rounded-xl bg-[var(--info-light)] p-5 text-sm text-[var(--info-text)]"
      data-testid="werkkwaliteit-ai-nog-niet-klaar"
      role="status"
    >
      <p className="font-semibold">{kop}</p>
      <p className="mt-1">{tekst}</p>
    </div>
  );
}
